import React, { useMemo, useCallback } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import { autocompletion } from "@codemirror/autocomplete";

const SqlEditor = ({ value, onChange, height = "200px", schema = [] }) => {
  // Convert schema to CodeMirror format: { tableName: ["col1", "col2"] }
  const schemaConfig = useMemo(() => {
    const tables = {};
    schema.forEach((table) => {
      if (table.table_name && table.columns) {
        tables[table.table_name] = table.columns.map((col) => col.name);
      }
    });
    return tables;
  }, [schema]);

  // Create custom completion source with type hints
  const customCompletions = useCallback(
    (context) => {
      const word = context.matchBefore(/[\w.]+/);
      if (!word || (word.from === word.to && !context.explicit)) return null;

      const text = word.text;
      const options = [];

      // Check if we're completing after a table name (e.g., "users.")
      if (text.includes(".")) {
        const [tableName] = text.split(".");
        const table = schema.find(
          (t) => t.table_name.toLowerCase() === tableName.toLowerCase()
        );
        if (table && table.columns) {
          table.columns.forEach((col) => {
            options.push({
              label: col.name,
              type: "property",
              detail: col.type,
              info: `${tableName}.${col.name} (${col.type})${col.nullable ? " - nullable" : ""}`,
              boost: 1,
            });
          });
        }
      } else {
        // Suggest table names
        schema.forEach((table) => {
          if (table.table_name.toLowerCase().startsWith(text.toLowerCase())) {
            options.push({
              label: table.table_name,
              type: "class",
              detail: "table",
              info: `Table with ${table.columns?.length || 0} columns`,
              boost: 2,
            });
          }
        });

        // Also suggest all columns (for SELECT without table prefix)
        schema.forEach((table) => {
          if (table.columns) {
            table.columns.forEach((col) => {
              if (col.name.toLowerCase().startsWith(text.toLowerCase())) {
                options.push({
                  label: `${table.table_name}.${col.name}`,
                  type: "property",
                  detail: col.type,
                  info: `From ${table.table_name}`,
                  boost: 0,
                });
              }
            });
          }
        });
      }

      if (options.length === 0) return null;

      return {
        from: word.from,
        options,
        validFor: /^[\w.]*$/,
      };
    },
    [schema]
  );

  // Create SQL extension with schema
  const sqlExtension = useMemo(() => {
    return sql({
      dialect: PostgreSQL,
      schema: schemaConfig,
      upperCaseKeywords: true,
    });
  }, [schemaConfig]);

  // Combined extensions
  const extensions = useMemo(() => {
    const exts = [sqlExtension];
    if (schema.length > 0) {
      exts.push(
        autocompletion({
          override: [customCompletions],
          activateOnTyping: true,
          maxRenderedOptions: 50,
        })
      );
    }
    return exts;
  }, [sqlExtension, schema, customCompletions]);

  return (
    <div className="border border-D0D5DD rounded-md overflow-hidden">
      <CodeMirror
        value={value}
        height={height}
        extensions={extensions}
        onChange={onChange}
        theme="light"
        placeholder="Enter your SQL query here... (e.g., SELECT * FROM sales_order LIMIT 10). Press Ctrl+Space for autocomplete."
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightSpecialChars: true,
          foldGutter: true,
          drawSelection: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          rectangularSelection: true,
          crosshairCursor: true,
          highlightActiveLine: true,
          highlightSelectionMatches: true,
          closeBracketsKeymap: true,
          searchKeymap: true,
          foldKeymap: true,
          completionKeymap: true,
          lintKeymap: true,
        }}
      />
    </div>
  );
};

export default SqlEditor;
